import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getVc } from "../lib/api.js";
import VcVoteWidget from "../components/VcVoteWidget.js";
import VibeBar from "../components/VibeBar.js";
import ControversyCard from "../components/ControversyCard.js";
import {
  VC_TYPE_COLORS,
  VC_TYPE_LABELS,
  VC_REASON_TAGS,
} from "../lib/constants.js";

function vibeScoreDisplay(score: number): number {
  return Math.round(((score + 2) / 4) * 100);
}

function vibeGradient(score: number): string {
  if (score >= 1) return "from-emerald-500/15 to-green-500/10 border-emerald-500/20";
  if (score >= 0.3) return "from-lime-500/15 to-emerald-500/10 border-lime-500/20";
  if (score >= -0.3) return "from-yellow-500/15 to-amber-500/10 border-yellow-500/20";
  if (score >= -1) return "from-orange-500/15 to-red-500/10 border-orange-500/20";
  return "from-red-500/15 to-rose-500/10 border-red-500/20";
}

function vibeTextColor(score: number): string {
  if (score >= 1) return "text-emerald-400";
  if (score >= 0.3) return "text-lime-400";
  if (score >= -0.3) return "text-yellow-400";
  if (score >= -1) return "text-orange-400";
  return "text-red-400";
}

function fragilityLabel(score: number): string {
  if (score <= 1.5) return "Very Resilient";
  if (score <= 2.5) return "Resilient";
  if (score <= 3.5) return "Mixed";
  if (score <= 4.5) return "Fragile";
  return "Very Fragile";
}

function fragilityGradient(score: number): string {
  if (score <= 2) return "from-emerald-500/15 to-green-500/10 border-emerald-500/20";
  if (score <= 3.5) return "from-yellow-500/15 to-amber-500/10 border-yellow-500/20";
  return "from-red-500/15 to-orange-500/10 border-red-500/20";
}

function confidenceLabel(score: number): string {
  if (score >= 60) return "High";
  if (score >= 30) return "Medium";
  return "Low";
}

function aumLabel(bucket: string | null): string {
  if (!bucket) return "N/A";
  const labels: Record<string, string> = {
    "under_100m": "< $100M",
    "100m_500m": "$100M - $500M",
    "500m_1b": "$500M - $1B",
    "1b_5b": "$1B - $5B",
    "5b_plus": "$5B+",
  };
  return labels[bucket] || bucket.replace(/_/g, " ");
}

function vibeScoreColorClass(score: number): string {
  if (score >= 1) return "bg-emerald-500/15 text-emerald-400 border-emerald-500/25";
  if (score >= 0.3) return "bg-lime-500/15 text-lime-400 border-lime-500/25";
  if (score >= -0.3) return "bg-yellow-500/15 text-yellow-400 border-yellow-500/25";
  if (score >= -1) return "bg-orange-500/15 text-orange-400 border-orange-500/25";
  return "bg-red-500/15 text-red-400 border-red-500/25";
}

export default function VcDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [vc, setVc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getVc(slug)
      .then(setVc)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleShareX() {
    const text = `Check out ${vc.name} on StartupVibes \u2014 vibe score: ${vibeScoreDisplay(vc.vibe_score)}/100`;
    const url = window.location.href;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      "_blank"
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse space-y-6">
        <div className="h-4 skeleton rounded w-24" />
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 skeleton rounded-2xl" />
          <div className="flex-1 space-y-3">
            <div className="h-8 skeleton rounded w-1/3" />
            <div className="h-4 skeleton rounded w-2/3" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 skeleton rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !vc) {
    return (
      <div className="text-center py-20 animate-fade-in">
        <div className="text-5xl mb-4">{"\u{1F50D}"}</div>
        <h1 className="text-2xl font-bold text-slate-300 mb-2">
          VC not found
        </h1>
        <p className="text-slate-500 mb-6">
          This VC might not exist or was removed.
        </p>
        <Link to="/vcs" className="btn-secondary">
          {"\u2190"} Back to VC feed
        </Link>
      </div>
    );
  }

  const vibeDisplay = vibeScoreDisplay(vc.vibe_score);
  const color = vibeTextColor(vc.vibe_score);
  const isTrending = vc.trend_score > 0;
  const typeColor = VC_TYPE_COLORS[vc.type] || "bg-slate-500/15 text-slate-300 border-slate-500/20";
  const typeLabel = VC_TYPE_LABELS[vc.type] || vc.type?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) || "VC";
  const controversies = vc.controversies || [];
  const portfolio = vc.portfolio || [];

  // Severity breakdown
  const severityBreakdown = controversies.reduce(
    (acc: Record<string, number>, c: any) => {
      acc[c.severity] = (acc[c.severity] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Back nav */}
      <Link
        to="/vcs"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors mb-6 group"
      >
        <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to VC feed
      </Link>

      {/* Header */}
      <div className="flex items-start gap-5 mb-8">
        <div className="w-20 h-20 rounded-2xl bg-slate-800/80 flex items-center justify-center overflow-hidden shrink-0 border border-slate-700/30">
          <span className="text-3xl font-bold text-slate-500">
            {vc.name.charAt(0)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-extrabold text-white">
              {vc.name}
            </h1>
            {isTrending && (
              <span className="badge-trending">{"\u{1F525}"} Trending</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            {vc.domain && (
              <a
                href={`https://${vc.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
              >
                {vc.domain} {"\u2197"}
              </a>
            )}
            <span className={`badge border text-xs ${typeColor}`}>
              {typeLabel}
            </span>
            {vc.hq_city && (
              <span className="text-sm text-slate-500">
                {"\u{1F4CD}"} {vc.hq_city}{vc.hq_country ? `, ${vc.hq_country}` : ""}
              </span>
            )}
            {vc.founded_year && (
              <span className="text-sm text-slate-500">
                Est. {vc.founded_year}
              </span>
            )}
            {vc.aum_bucket && (
              <span className="badge bg-slate-800/60 text-slate-400 border border-slate-700/30 text-xs">
                AUM: {aumLabel(vc.aum_bucket)}
              </span>
            )}
          </div>
          {vc.description && (
            <p className="text-slate-400 mt-3 leading-relaxed">
              {vc.description}
            </p>
          )}

          {/* Share buttons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCopyLink}
              className="btn-ghost text-xs flex items-center gap-1.5 !px-3 !py-1.5 border border-slate-700/30"
            >
              {copied ? (
                <>
                  <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy link
                </>
              )}
            </button>
            <button
              onClick={handleShareX}
              className="btn-ghost text-xs flex items-center gap-1.5 !px-3 !py-1.5 border border-slate-700/30"
            >
              Share on X
            </button>
          </div>
        </div>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div
          className={`bg-gradient-to-br ${vibeGradient(vc.vibe_score)} border rounded-2xl p-5 text-center`}
        >
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Vibe Score
          </div>
          <div className={`text-4xl font-extrabold ${color}`}>
            {vibeDisplay}
          </div>
          <div className="text-xs text-slate-500 mt-1">/ 100</div>
        </div>
        <div
          className={`bg-gradient-to-br ${fragilityGradient(vc.fragility_score || 3)} border rounded-2xl p-5 text-center`}
        >
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Outlook
          </div>
          <div className="text-2xl font-bold text-white">
            {fragilityLabel(vc.fragility_score || 3)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-2 font-medium">
            Confidence
          </div>
          <div className="text-2xl font-bold text-white">
            {confidenceLabel(vc.confidence_score || 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {vc.confidence_score || 0}/100
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700/30 rounded-2xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-2 font-medium">Votes</div>
          <div className="text-2xl font-bold text-white">
            {vc.trusted_vote_count}
          </div>
          <div className="text-xs text-slate-500 mt-1">trusted</div>
        </div>
      </div>

      {/* Fraud Scoreboard / Controversy Tracker */}
      {controversies.length > 0 && (
        <div className="mb-8 bg-gradient-to-br from-red-500/8 to-red-900/5 border border-red-500/20 rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-bold text-lg text-red-300 flex items-center gap-2">
              <span>{"\u{1F6A8}"}</span> Controversy Tracker
            </h3>
            <div className="flex items-center gap-3">
              <span className="badge bg-red-500/15 text-red-400 border border-red-500/25 text-sm font-semibold">
                {controversies.length} {controversies.length === 1 ? "issue" : "issues"}
              </span>
              {vc.fraud_score > 0 && (
                <span className="badge bg-red-500/15 text-red-400 border border-red-500/25 text-sm font-semibold">
                  Fraud Score: {Math.round(vc.fraud_score)}%
                </span>
              )}
            </div>
          </div>

          {/* Severity breakdown */}
          <div className="flex gap-3 flex-wrap">
            {(["critical", "high", "medium", "low"] as const).map((sev) => {
              const count = severityBreakdown[sev] || 0;
              if (count === 0) return null;
              const sevColors: Record<string, string> = {
                critical: "bg-red-500/20 text-red-400 border-red-500/30",
                high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
                medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
                low: "bg-slate-500/20 text-slate-400 border-slate-500/30",
              };
              return (
                <span
                  key={sev}
                  className={`px-3 py-1 rounded-lg border text-sm font-medium ${sevColors[sev]}`}
                >
                  {sev.charAt(0).toUpperCase() + sev.slice(1)}: {count}
                </span>
              );
            })}
          </div>

          {/* Controversy cards */}
          <div className="space-y-3">
            {controversies.map((c: any) => (
              <ControversyCard key={c.id} controversy={c} />
            ))}
          </div>
        </div>
      )}

      {/* Portfolio section */}
      {portfolio.length > 0 && (
        <div className="card mb-8">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>{"\u{1F4BC}"}</span> Portfolio ({portfolio.length} companies)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {portfolio.map((company: any) => (
              <Link
                key={company.id || company.slug}
                to={`/startup/${company.slug}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/40 border border-slate-700/20 hover:border-indigo-500/30 hover:bg-slate-800/60 transition-all group"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0 border border-slate-600/30">
                  <span className="text-xs font-bold text-slate-400 group-hover:text-slate-300 transition-colors">
                    {company.name?.charAt(0) || "?"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-200 group-hover:text-white truncate transition-colors">
                    {company.name}
                  </div>
                  {company.round && (
                    <div className="text-xs text-slate-500">
                      {company.round.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </div>
                  )}
                </div>
                {company.vibe_score !== undefined && (
                  <span className={`text-sm font-bold px-2 py-0.5 rounded-lg border ${vibeScoreColorClass(company.vibe_score)}`}>
                    {vibeScoreDisplay(company.vibe_score)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Distributions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Vibe distribution */}
        {vc.vibe_distribution && (
          <div className="card">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <span>{"\u{1F4CA}"}</span> Vibe Distribution
            </h3>
            <VibeBar distribution={vc.vibe_distribution} size="lg" />
            <div className="grid grid-cols-5 gap-1 mt-3">
              {[-2, -1, 0, 1, 2].map((v) => (
                <div key={v} className="text-center">
                  <div className="text-lg font-bold text-slate-300">
                    {vc.vibe_distribution[v] || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tag distribution */}
        {vc.tag_distribution &&
          Object.keys(vc.tag_distribution).length > 0 && (
            <div className="card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <span>{"\u{1F3F7}\uFE0F"}</span> Top Tags
              </h3>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(vc.tag_distribution)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([tag, count]) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-slate-800/60 text-sm text-slate-300 border border-slate-700/30 font-medium"
                    >
                      {VC_REASON_TAGS.find((r) => r.value === tag)?.label || tag}{" "}
                      <span className="text-slate-500 ml-1">
                        {count as number}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
          )}
      </div>

      {/* Vote widget */}
      <VcVoteWidget
        vcId={vc.id}
        vcName={vc.name}
        onVoted={() => {
          if (slug) getVc(slug).then(setVc);
        }}
      />
    </div>
  );
}
